import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState, type ReactNode, type Key } from 'react';
import { Table, TableBody, useDragAndDrop, type Selection, type SortDescriptor } from 'react-aria-components';
import { Button } from '../components/buttons/Button';
import { DataTablePager, type DataTablePagerConfig } from './DataTablePager';
import { Loading } from '../Loading';
import { DataTableHeader, type DataTableColumnProps } from './DataTableColumn';
import type { DataTableRowWithId } from './types';
import { DataTableRow } from './DataTableRow';
import { cn } from '../components/utils';

export type DataTableSelectionConfig<T = unknown> = {
	selectedIds: string[];
	onSelectionChange: (ids: string[]) => void;
	onSelectionRowsChange?: (rows: T[]) => void;
	selectionToolbarSlot?: ReactNode;
};

export type DataTableProps<T extends object> = {
	columns: DataTableColumnProps<T>[];
	rows: T[];
	getRowId?: (row: T) => string;
	getRowKey?: (row: T, index: number) => string | number;
	emptyMessage?: ReactNode;
	className?: string;
	viewMore?: {
		text: string;
		threshold: number;
	};
	pager?: DataTablePagerConfig;
	getRowNotes?: (row: T) => string | null;
	renderRowDetail?: (row: T) => ReactNode;
	getRowClassName?: (row: T) => string;
	rowLabel?: string;
	onRowClick?: (row: T) => void;
	allowDragAndDrop?: boolean;
	onSorted?: (ids: (string | number)[]) => void;
	isLoading?: boolean;
	selection?: DataTableSelectionConfig<T>;
	sortDescriptor?: SortDescriptor;
	onSortChange?: (descriptor: SortDescriptor) => void;
	ariaLabel?: string;
	getMuteRowOpacity?: (row: T) => boolean;
	hideColumns?: string[];
};
export function DataTable<T extends object>({
	columns,
	rows,
	getRowId,
	getRowKey,
	emptyMessage = 'No records',
	className = '',
	viewMore,
	pager,
	rowLabel = 'Notes',
	onRowClick,
	getRowNotes,
	renderRowDetail,
	getRowClassName,
	allowDragAndDrop = false,
	onSorted,
	isLoading = false,
	selection,
	sortDescriptor,
	onSortChange,
	ariaLabel = 'Data table',
	hideColumns,
	getMuteRowOpacity
}: DataTableProps<T>) {
	const [expanded, setExpanded] = useState(false);
	const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
	const [clientPage, setClientPage] = useState(1);

	const visibleColumns = columns.filter(col => !hideColumns?.includes(col.key));

	const getId = (row: T): string => (getRowId ? getRowId(row) : (row as DataTableRowWithId).id);
	const usePager = !!pager;
	const serverPaged = pager?.mode === 'server';

	const totalCount = serverPaged ? pager.totalCount : rows.length;
	const totalPages = usePager ? Math.max(1, Math.ceil(totalCount / pager.pageSize)) : 1;
	const currentPage = serverPaged ? pager.currentPage : Math.min(clientPage, totalPages);
	const onPageChange = serverPaged ? pager.onPageChange : setClientPage;
	const onPageSizeChange = serverPaged ? pager.onPageSizeChange : undefined;

	const visibleRows =
		usePager && !serverPaged
			? rows.slice((currentPage - 1) * pager.pageSize, currentPage * pager.pageSize)
			: !usePager && !expanded && viewMore
			? rows.slice(0, viewMore.threshold)
			: rows;

	const dndEnabled = allowDragAndDrop && !usePager;

	const showSelectionCol = !!selection;
	const showDragCol = dndEnabled;
	const showExpandCol = (!!getRowNotes && rows.map(x => getRowNotes(x)).filter(x => !!x).length > 0) || !!renderRowDetail;
	const hasLeading = showSelectionCol || showDragCol || showExpandCol;

	const getRowDetail = (row: T): ReactNode => {
		if (renderRowDetail) {
			return renderRowDetail(row);
		}
		const note = getRowNotes?.(row);
		if (!note) {
			return null;
		}
		return (
			<p className="text-xs leading-relaxed text-gray-700">
				<span className="mr-1 font-semibold text-gray-900">{rowLabel}:</span>
				{note}
			</p>
		);
	};

	const selectedSet = new Set(selection?.selectedIds ?? []);
	const allSelected = !!selection && rows.length > 0 && rows.every(r => selectedSet.has(getId(r)));
	const selectAll = () => selection?.onSelectionChange(rows.map(r => getId(r)));
	const deselectAll = () => selection?.onSelectionChange([]);

	const emitSelection = (nextIds: string[]) => {
		selection!.onSelectionChange(nextIds);
		if (selection!.onSelectionRowsChange) {
			const idSet = new Set(nextIds);
			selection!.onSelectionRowsChange(rows.filter(r => idSet.has(getId(r))));
		}
	};

	const handleSelectionChange = (keys: Selection) => {
		if (!selection) return;
		const visible = new Set(visibleRows.map(r => getId(r)));
		const offPage = selection.selectedIds.filter(id => !visible.has(id));
		if (keys === 'all') {
			emitSelection([...offPage, ...visibleRows.map(r => getId(r))]);
			return;
		}
		const onPage = [...keys].map(k => String(k)).filter(id => visible.has(id));
		emitSelection([...offPage, ...onPage]);
	};

	const reorder = (movedKeys: Set<Key>, targetKey: Key, position: 'before' | 'after') => {
		const moved = new Set<string>();
		movedKeys.forEach(k => moved.add(String(k)));
		const remaining = rows.filter(r => !moved.has(getId(r)));
		let targetIndex = remaining.findIndex(r => getId(r) === String(targetKey));
		if (targetIndex === -1) return;
		if (position === 'after') targetIndex += 1;
		const movedRows = rows.filter(r => moved.has(getId(r)));
		remaining.splice(targetIndex, 0, ...movedRows);
		onSorted?.(remaining.map(r => getId(r)));
	};

	const { dragAndDropHooks } = useDragAndDrop({
		getItems: keys =>
			[...keys].map(key => ({
				'text/plain': String(key)
			})),
		onDragStart() {
			setExpandedRowId(null);
		},
		onReorder(e) {
			if (e.target.dropPosition === 'before' || e.target.dropPosition === 'after') {
				reorder(e.keys, e.target.key, e.target.dropPosition);
			}
		}
	});

	const detailRowKeys = showExpandCol
		? visibleRows.filter(r => expandedRowId === getId(r) && !!getRowDetail(r)).map(r => `${getId(r)}__detail`)
		: [];

	if (isLoading && rows.length === 0) {
		return (
			<div className="flex items-center justify-center rounded-xl border border-gray-200 p-12 shadow-sm">
				<Loading />
			</div>
		);
	}
	return (
		<div className="relative flex flex-col overflow-hidden rounded-xl border border-gray-200 shadow-sm">
			{isLoading && (
				<div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]">
					<Loading />
				</div>
			)}
			{selection && (
				<div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2">
					<p className="text-xs text-gray-600">
						{selection.selectedIds.length} of {rows.length} selected
					</p>
					<div className="flex gap-2">
						{selection.selectionToolbarSlot}
						<Button appearance="outline" size="sm" onClick={selectAll} isDisabled={allSelected} label="Select All" />
						<Button
							appearance="outline"
							size="sm"
							onClick={deselectAll}
							isDisabled={selection.selectedIds.length === 0}
							label="Deselect All"
						/>
					</div>
				</div>
			)}
			<Table
				aria-label={ariaLabel}
				className={cn('w-full text-xs bg-white', className)}
				selectionMode={showSelectionCol ? 'multiple' : 'none'}
				selectionBehavior="toggle"
				disabledBehavior="selection"
				disabledKeys={detailRowKeys}
				selectedKeys={showSelectionCol ? new Set(selection!.selectedIds) : undefined}
				onSelectionChange={showSelectionCol ? handleSelectionChange : undefined}
				sortDescriptor={sortDescriptor}
				onSortChange={onSortChange}
				dragAndDropHooks={showDragCol ? dragAndDropHooks : undefined}
			>
				<DataTableHeader
					columns={visibleColumns}
					showSelectionCol={showSelectionCol}
					showDragCol={showDragCol}
					showExpandCol={showExpandCol}
					hasLeading={hasLeading}
				/>
				<TableBody renderEmptyState={() => <div className="px-3 py-6 text-center text-xs text-gray-500">{emptyMessage}</div>}>
					{visibleRows.map((row, i) => {
						const rowId = getId(row);
						const detailContent = getRowDetail(row);
						const hasDetail = !!detailContent;
						const detailExpanded = expandedRowId === rowId;
						const rowKey = getRowKey?.(row, i) ?? rowId;
						const rowClassName = cn(getRowClassName && getRowClassName(row), getMuteRowOpacity && getMuteRowOpacity(row) && 'opacity-60');
						return (
							<DataTableRow<T>
								key={rowKey}
								rowId={rowId}
								row={row}
								index={i}
								columns={visibleColumns}
								showSelectionCol={showSelectionCol}
								showDragCol={showDragCol}
								showExpandCol={showExpandCol}
								hasLeading={hasLeading}
								rowClassName={rowClassName}
								detailContent={detailContent}
								hasDetail={hasDetail}
								detailExpanded={detailExpanded}
								onToggleDetail={() => setExpandedRowId(detailExpanded ? null : rowId)}
								onRowClick={onRowClick}
							/>
						);
					})}
				</TableBody>
			</Table>
			{usePager ? (
				totalCount > 0 && (
					<DataTablePager
						currentPage={currentPage}
						totalPages={totalPages}
						totalCount={totalCount}
						pageSize={pager.pageSize}
						onPageChange={onPageChange}
						onPageSizeChange={onPageSizeChange}
					/>
				)
			) : viewMore && rows.length > viewMore.threshold ? (
				<div className="border-t border-gray-100 px-3 py-2">
					<button
						type="button"
						onClick={() => setExpanded(!expanded)}
						className="flex items-center gap-1.5 text-xs font-medium text-gray-600 transition-colors hover:text-gray-900"
					>
						{expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
						{expanded ? 'Hide' : 'View'} {viewMore.text} ({rows.length - viewMore.threshold})
					</button>
				</div>
			) : null}
		</div>
	);
}
